import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Megaphone, Award, Users, Phone, QrCode, ArrowRight, ChevronRight,
  Clock, AlertCircle, Play, Instagram, Calendar,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/api/client";
import { useI18n } from "@/i18n";
import { vhrpImages, galleryImages } from "@/lib/site";
import DigitalCard from "@/components/DigitalCard";
import { Button } from "@/components/ui/button";

const Section = ({ children, className = "" }) => (
  <div className={`mx-auto max-w-7xl px-4 ${className}`}>{children}</div>
);

export default function Home() {
  const { user, loginWithGoogle } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    (async () => {
      const [n, e, g] = await Promise.all([
        api("/news").catch(() => []),
        api("/events").catch(() => []),
        api("/gallery").catch(() => []),
      ]);
      setNews(n);
      setEvents(e);
      setGallery(g);
      api("/videos", { skipAuth: true }).then((r) => setVideos(r.videos || [])).catch(() => {});
      if (user) {
        api("/membership/me").then((m) => setMember(m.member)).catch(() => {});
      }
    })();
  }, [user]);

  const isActive = member?.status === "active";
  const daysLeft = member?.valid_until
    ? Math.ceil((new Date(member.valid_until).getTime() - Date.now()) / 86400000)
    : null;
  const showRenew = isActive && daysLeft !== null && daysLeft <= 30;
  const isExpired = showRenew && daysLeft < 0;

  const tiles = [
    { icon: Megaphone, label: t("quickGrievance"), to: "/grievance", testid: "tile-grievance" },
    { icon: Award, label: t("tabMembership"), to: "/membership", testid: "tile-membership" },
    { icon: Users, label: t("dirTile"), to: "/directory", testid: "tile-directory" },
    { icon: Phone, label: t("quickContact"), to: "/contact", testid: "tile-contact" },
  ];

  return (
    <div className="pb-4">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <img src={vhrpImages.banner} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(230,81,0,0.82), rgba(191,54,12,0.95))" }} />
        <Section className="relative py-16 lg:py-24">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="inline-block rounded-full border border-white/40 bg-white/15 px-4 py-1 text-xs font-bold tracking-wide text-white">
                {t("motto")}
              </span>
              <h1 className="mt-4 text-4xl font-black leading-tight text-white sm:text-5xl">
                {t("orgName")}
              </h1>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-white/90">{t("heroSub")}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                {isActive ? (
                  <Button data-testid="hero-view-card" onClick={() => navigate("/card")} className="h-12 rounded-full bg-white px-6 font-bold text-brand-dark hover:bg-white/90">
                    {t("cardHeadTitle")} <ArrowRight size={16} className="ml-1" />
                  </Button>
                ) : (
                  <Button data-testid="hero-join" onClick={() => navigate("/membership")} className="h-12 rounded-full bg-white px-6 font-bold text-brand-dark hover:bg-white/90">
                    {t("heroCta")} <ArrowRight size={16} className="ml-1" />
                  </Button>
                )}
                {!user && (
                  <Button data-testid="hero-login" onClick={loginWithGoogle} variant="outline" className="h-12 rounded-full border-white/60 bg-transparent px-6 font-bold text-white hover:bg-white/10 hover:text-white">
                    {t("loginGoogle")}
                  </Button>
                )}
              </div>
            </div>
            <div className="hidden lg:block">
              {isActive ? (
                <div className="max-w-md" onClick={() => navigate("/card")} role="button">
                  <DigitalCard member={member} showQr />
                </div>
              ) : (
                <div className="ml-auto max-w-md rounded-3xl border border-white/20 bg-white/10 p-6 backdrop-blur">
                  <img src={vhrpImages.logoMark} alt="" className="h-16 w-16 rounded-full" />
                  <h3 className="mt-4 text-xl font-bold text-white">{t("unlockTitlePending") || t("unlockTitle")}</h3>
                  <p className="mt-2 text-sm text-white/80">{t("unlockSubPending") || t("unlockSub")}</p>
                </div>
              )}
            </div>
          </div>
        </Section>
      </div>

      {/* Renewal banner */}
      {showRenew && (
        <Section className="pt-6">
          <button
            data-testid="renew-banner"
            onClick={() => navigate("/membership")}
            className={`flex w-full items-center gap-3 rounded-xl border p-4 text-left ${isExpired ? "border-[#F2B8B5] bg-[#FDECEA]" : "border-[#FFCC80] bg-[#FFF3E0]"}`}
          >
            {isExpired ? <AlertCircle className="text-[#C62828]" /> : <Clock className="text-brand" />}
            <div className="flex-1">
              <div className={`text-sm font-bold ${isExpired ? "text-[#C62828]" : "text-brand"}`}>
                {isExpired ? t("renewExpiredTitle") : t("renewSoonTitle")}
              </div>
              <div className="text-xs text-ink-soft">
                {isExpired ? t("renewExpiredBody") : t("renewSoonBody").replace("{days}", String(daysLeft))}
              </div>
            </div>
            <span className={`rounded-full px-4 py-1.5 text-xs font-bold text-white ${isExpired ? "bg-[#C62828]" : "bg-brand"}`}>
              {t("renewCta")}
            </span>
          </button>
        </Section>
      )}

      {/* Quick tiles */}
      <Section className="pt-8">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {tiles.map((tile) => (
            <Link key={tile.to} to={tile.to} data-testid={tile.testid} className="group flex flex-col items-center gap-3 rounded-2xl border border-line bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-brand hover:shadow-md">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-tint text-brand transition-colors group-hover:bg-brand group-hover:text-white">
                <tile.icon size={22} />
              </span>
              <span className="text-center text-sm font-semibold text-ink">{tile.label}</span>
            </Link>
          ))}
        </div>
        <Link to="/verify" data-testid="verify-row" className="mt-3 flex items-center gap-3 rounded-2xl border border-brand/30 bg-white p-4 hover:border-brand">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white"><QrCode size={20} /></span>
          <span className="flex-1 text-sm font-bold text-ink">{t("verifyTile")}</span>
          <ChevronRight size={18} className="text-brand" />
        </Link>
      </Section>

      {/* Events */}
      {events.length > 0 && (
        <Section className="pt-10">
          <SectionHead title={t("eventsTitle")} to="/events" seeAll={t("seeAll")} />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {events.slice(0, 3).map((ev) => {
              const d = new Date(ev.date);
              return (
                <Link key={ev.id} to="/events" data-testid={`home-event-${ev.id}`} className="flex gap-4 rounded-xl border border-line bg-white p-4 hover:border-brand">
                  <div className="flex h-14 w-14 flex-col items-center justify-center rounded-lg bg-brand text-white">
                    <span className="text-lg font-black leading-none">{d.toLocaleDateString("en-IN", { day: "2-digit" })}</span>
                    <span className="text-[10px] font-bold uppercase">{d.toLocaleDateString("en-IN", { month: "short" })}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="line-clamp-2 text-sm font-bold text-ink">{ev.title}</div>
                    <div className="mt-1 truncate text-xs text-ink-muted">📍 {ev.venue}</div>
                    <div className="mt-1 text-xs font-bold text-brand">
                      {ev.my_rsvp ? t("rsvpGoing") : `${ev.rsvp_count} ${t("attending")}`}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </Section>
      )}

      {/* Founder + stats */}
      <Section className="pt-12">
        <div className="overflow-hidden rounded-3xl bg-[#29211D] lg:grid lg:grid-cols-[240px_1fr]">
          <img src={vhrpImages.founder} alt="Founder" className="h-64 w-full object-cover lg:h-full" />
          <div className="p-7">
            <div className="text-xs font-black uppercase tracking-widest text-brand-tint">{t("founderMsgTitle")}</div>
            <p className="mt-3 text-lg italic leading-relaxed text-white">“{t("founderQuote")}”</p>
            <div className="mt-4 font-bold text-white">{t("founderName")}</div>
            <div className="text-sm text-white/70">{t("founderRole")}</div>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-3 gap-3 rounded-2xl border border-brand/20 bg-brand-tint p-5">
          <Stat n="500+" label={t("statPrograms")} />
          <Stat n="50,000+" label={t("statRelief")} />
          <Stat n="100+" label={t("statCenters")} />
        </div>
      </Section>

      {/* Gallery */}
      <Section className="pt-12">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-extrabold text-ink">{t("galleryTitle")}</h2>
          <a href="https://www.instagram.com/iGopalRai" target="_blank" rel="noreferrer" data-testid="instagram-link" className="flex items-center gap-1.5 rounded-full bg-[#C13584] px-3 py-1.5 text-xs font-bold text-white">
            <Instagram size={14} /> Instagram
          </a>
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {(gallery.length ? gallery.map((g) => g.image) : galleryImages).map((src) => (
            <img key={src} src={src} alt="" className="h-40 w-56 flex-shrink-0 rounded-xl object-cover" />
          ))}
        </div>
      </Section>

      {/* Videos */}
      {videos.length > 0 && (
        <Section className="pt-12">
          <SectionHead title={t("videosTitle")} to="/videos" seeAll={t("seeAll")} />
          <div className="flex gap-4 overflow-x-auto pb-2">
            {videos.slice(0, 6).map((v) => (
              <Link key={v.video_id} to="/videos" data-testid={`home-video-${v.video_id}`} className="w-56 flex-shrink-0">
                <div className="relative">
                  <img src={v.thumbnail} alt="" className="h-32 w-56 rounded-xl object-cover" />
                  <span className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/80 bg-brand-deep/90 text-white">
                    <Play size={16} fill="currentColor" />
                  </span>
                </div>
                <div className="mt-2 line-clamp-2 text-xs font-semibold text-ink">{v.title}</div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* News */}
      <Section className="pt-12">
        <SectionHead title={t("latestUpdates")} to="/news" seeAll={t("seeAll")} />
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {news.slice(0, 6).map((n) => (
            <Link key={n.id} to="/news" data-testid={`news-${n.id}`} className="overflow-hidden rounded-2xl border border-line bg-white transition-all hover:-translate-y-0.5 hover:shadow-md">
              {n.image && <img src={n.image} alt="" className="h-44 w-full object-cover" />}
              <div className="p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-brand">{n.category}</div>
                <div className="mt-1.5 line-clamp-2 font-bold text-ink">{n.title}</div>
                <div className="mt-1.5 line-clamp-2 text-sm text-ink-muted">{n.summary}</div>
              </div>
            </Link>
          ))}
        </div>
      </Section>
    </div>
  );
}

function SectionHead({ title, to, seeAll }) {
  return (
    <div className="mb-4 flex items-center justify-between">
      <h2 className="text-xl font-extrabold text-ink">{title}</h2>
      <Link to={to} className="flex items-center gap-1 text-sm font-bold text-brand hover:underline">
        {seeAll} <ArrowRight size={14} />
      </Link>
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div className="text-center">
      <div className="text-xl font-black text-brand-dark sm:text-2xl">{n}</div>
      <div className="mt-1 text-[11px] font-medium text-ink-soft">{label}</div>
    </div>
  );
}
